import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationApi, ViewerDetail, errorMessage } from '../services/GraduationApi';
import { formatSubmissionDate as date } from '../services/SubmissionDate';
import { useCanViewUndergraduateApplications } from './StudentWorkspace';


export default function ViewerApplicationDetails() {
  const route = useParams(); const [query] = useSearchParams();
  const id = Number(route.id || query.get('applicationId'));
  const allowed = useCanViewUndergraduateApplications();
  const back = `/admin/undergraduate${query.get('list') ? `?${query.get('list')}` : ''}`;
  const [detail, setDetail] = useState<ViewerDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!allowed || !Number.isInteger(id) || id <= 0) return;
    const controller = new AbortController();
    setLoading(true); setError(''); setDetail(null);
    GraduationApi.viewerDetail(id, controller.signal)
      .then(result => { if (!controller.signal.aborted) setDetail(result); })
      .catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [allowed, id, revision]);
  if (!allowed) return <Navigate to="/" replace />;
  if (!Number.isInteger(id) || id <= 0) return <Alert variant="danger">A valid application ID is required. <Link to="/admin/undergraduate">Return to applications</Link></Alert>;
  const download = async (documentId: number, name: string) => {
    try {
      const file = await GraduationApi.viewerDocument(id, documentId, new AbortController().signal);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a'); link.href = url; link.download = name; link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(errorMessage(e)); }
  };
  return <article className="admin-application-detail">
    <Link to={back} className="d-inline-block mb-3">← Back to undergraduate applications</Link>
    <header className="mb-4"><h1>Application Details</h1><p className="text-secondary">Undergraduate graduation application #{id} · Read only</p></header>
    {loading && <p role="status">Loading application…</p>}
    {error && <Alert variant="danger" role="alert">{error} <Button size="sm" variant="outline-danger" onClick={() => setRevision(v => v + 1)}>Try again</Button></Alert>}
    {detail && <>
      <section className="section-panel mb-4" aria-labelledby="viewer-status"><h2 id="viewer-status" className="h4">Application Status</h2>
        <p><strong>{detail.status}</strong>{detail.archived && ' · Archived'}</p>
        {detail.statusDescription && <p>{detail.statusDescription}</p>}
        {detail.statusId > 0 && <p>This application is closed for editing. For more information, contact Registration and Academic Records.</p>}
      </section>
      <section className="section-panel mb-4" aria-labelledby="viewer-student"><h2 id="viewer-student" className="h4">Student and graduation</h2>
        <dl className="data-summary"><dt>Legal name</dt><dd>{detail.legalName}</dd>
          <dt>Preferred name</dt><dd>{detail.preferredName || 'Not recorded'}</dd>
          <dt>Preferred diploma name</dt><dd>{detail.diplomaName || 'Not recorded'}</dd>
          <dt>Graduation term</dt><dd>{detail.graduationTerm || 'Not specified'}</dd>
          <dt>Submitted Date</dt><dd><time dateTime={detail.submitDate}>{date(detail.submitDate)}</time></dd></dl>
      </section>
      <section className="section-panel mb-4" aria-labelledby="viewer-areas"><h2 id="viewer-areas" className="h4">Area of Study</h2>
        {detail.areas.length ? <ul>{detail.areas.map((area, index) => <li key={`${area.areaStudyId}-${index}`}>
          {area.degree && `${area.degree}: `}{area.description || 'Area of study'}{area.areaStudyId && ` (${area.areaStudyId})`}{area.concentration && ` — ${area.concentration}`}
        </li>)}</ul> : <p>No areas of study recorded.</p>}
      </section>
      <section className="section-panel mb-4" aria-labelledby="viewer-documents"><h2 id="viewer-documents" className="h4">Application documents</h2>
        {detail.documents.length ? <AccessibleTable scrollLabel="Application documents" striped><thead><tr><th scope="col">Document</th><th scope="col">Submitted Date</th><th scope="col">Perceptive Content</th><th scope="col">Action</th></tr></thead>
          <tbody>{detail.documents.map(document => <tr key={document.id}><th scope="row">{document.name}</th>
            <td><time dateTime={document.submittedOn}>{date(document.submittedOn)}</time></td><td>{document.uploaded ? 'Uploaded' : 'Not uploaded'}</td>
            <td><Button size="sm" variant="outline-primary" onClick={() => download(document.id, document.name)}>Download</Button></td></tr>)}</tbody></AccessibleTable> : <p>No documents attached.</p>}
      </section>
      <section className="section-panel mb-4" aria-labelledby="viewer-notes"><h2 id="viewer-notes" className="h4">Application notes</h2>
        {detail.notes.length ? <AccessibleTable scrollLabel="Application notes" striped><thead><tr><th scope="col">Submitted by</th><th scope="col">Notes</th><th scope="col">Submitted Date</th></tr></thead>
          <tbody>{detail.notes.map(note => <tr key={note.id}><td>{note.author}</td><td className="note-text">{note.text}</td>
            <td><time dateTime={note.submittedOn}>{date(note.submittedOn)}</time></td></tr>)}</tbody></AccessibleTable> : <p>No application notes available.</p>}
      </section>
      <Button variant="outline-primary" onClick={() => window.print()}>Print to PDF</Button>
    </>}
  </article>;
}
